import React from 'react';
import { withMarketingLayout } from '~/marketing/MarketingLayout';
import { Post, Section, P, UL, PostLink, Sources, GraderCta } from '~/marketing/Post';
import { said, totals, count, word, homeCareSpellings } from '~/marketing/research/figures';

/* What AI assistants read before they name a business, and why the answer
   moves. Approved 2026-10-13 from `docs/blog/drafts.md`, post 2. The pest
   control and senior care sentences are held against the benchmark file by
   `benchmark_post_test`; every number is read through `figures.js`. */
const LISTED = '/blog/get-listed-where-ai-assistants-look';
const MEASURE = '/blog/how-to-measure-ai-visibility';

export default function HowAiAssistantsChoose() {
  return (
    <Post path="/blog/how-ai-assistants-choose-a-business" lede={<>
      No AI assistant publishes exactly how it chooses which businesses to name. What we can do is collect thousands of answers and look at what they have in common. This post draws on {count(totals.answers)} answers across {said.industries} local industries in {said.cities}.
    </>}>
      <Section title="They read, then answer">
        <P>
          Asked a local question, an AI assistant searches, writes its answer from the pages it found, and cites them. In our benchmark the citations fell into two main groups:
        </P>
        <UL>
          <li><strong>Places a business can be listed, reviewed or mentioned:</strong> directories, review sites, forums, roundup articles, news and reference sites. These were {said.listings} of citations, depending on the industry.</li>
          <li><strong>Businesses’ own websites:</strong> {said.own}.</li>
        </UL>
        <P>
          So an answer draws on two kinds of evidence: what others say about a business, and what the business says about itself.
        </P>
      </Section>

      <Section title="The question decides whether anyone is named">
        <P>
          Ask who to hire, or which company has the best reviews, and the answer names businesses: {count(totals.named)} of {count(totals.asked)} such answers did. Ask how to choose a business or what something should cost, and you mostly get advice: {count(totals.adviceNamed)} of {count(totals.advice)} of those answers named a business.
        </P>
        <P>
          So whether you are named is measured on the questions a customer asks when they are ready to choose.
        </P>
      </Section>

      <Section title="The answer changes each time you ask">
        <P>
          Ask the same AI assistant the same question again and the list moves. Of all the businesses named in two answers to the same question, {said.overlap} appeared in both. One answer on its own can mislead; the pattern across many answers is the thing to read.
        </P>
      </Section>

      <Section title="It changes between cities">
        <P>
          Who leads differs from city to city, even within one industry. In pest control, two national chains were the most-named companies in Dallas, came third and fourth in Nashville, and were outside the top four in Boise.
        </P>
      </Section>

      <Section title="One business, written many ways">
        <P>
          AI assistants write the same business in different ways: with and without “LLC”, with and without the city, sometimes by a former name. In answers about in-home senior care, one franchise brand was written as many as {word(homeCareSpellings)} ways in a single city.
        </P>
      </Section>

      <Section title="What you can influence">
        <P>You cannot choose what an AI assistant says. You can shape what it reads:</P>
        <UL>
          <li><PostLink to={LISTED}>Be listed and reviewed on the sites it cites</PostLink> for your industry and city.</li>
          <li>Publish pages that answer the questions your customers ask.</li>
          <li>Keep your business name the same everywhere.</li>
        </UL>
        <P>
          Then <PostLink to={MEASURE}>measure across repeated answers</PostLink> to see what changes.
        </P>
      </Section>

      <Sources />
      <GraderCta heading="See who AI assistants name in your city." />
    </Post>
  );
}

HowAiAssistantsChoose.layout = withMarketingLayout;
